import { VSBuffer } from "../../../../base/common/buffer";
import { BUILD_PLATFORM } from "../../../../base/common/$__metadata";
import { CancellationToken } from "../../../../base/common/cancellation";
import { Emitter, Event } from "../../../../base/common/events";
import { Disposable, IDisposable, toDisposable } from "../../../../base/common/lifecycle";
import { IAnalyticsService } from "../../../../platform/analytics/common/analytics";
import { IIdentitiyService } from "../../../../platform/auth/common/auth";
import { ILicenseService } from "../../../../platform/auth/common/licenses";
import { CommandsRegistry, ICommandService } from "../../../../platform/commands/common/commands";
import { IInstantiationService } from "../../../../platform/instantiation/common/instantiation";
import { ILogService } from "../../../../platform/log/common/log";
import { OpenUpsellDialogCommandId, type UpsellDialogOptions } from "../../../../platform/upsell/common/upsell";
import { URI } from "../../../../base/common/uri";
import { Asset, AssetFetchOptions, AssetFetchProgress, AssetFetchResult, AssetImportRequest, AssetInteraction, AssetOptionsResult, AssetResourceRef, AssetResourceResolution, AssetVersion, FullAsset, IAssetInteractionHandler, IAssetsService } from "./assets";
import { foldDevCloudSrc } from "../../needle/devCloudProxy";
import { GLTF_FAMILY_FORMATS, isGltfFamilyUrl } from "../../needle/engineFormats";
import "./commands"; // Register asset commands


/**
 * Picks which version of an asset to load. When a `src` is given (a document recorded the
 * exact version the user chose), match it by uri; otherwise fall back to the smallest by
 * `totalSize` — the same default the asset interaction handler uses so assign and reopen agree.
 * Exported so version UIs (inspector versions group) can mark the default consistently.
 */
export function pickAssetVersion(versions: AssetVersion[], src?: string): AssetVersion | undefined {
    if (versions.length === 0) return undefined;
    if (src) {
        // FOLD-normalized: documents record the host-relative proxied form while
        // providers list absolute urls — exact string equality silently fell back
        // to the smallest version on reopen (see devCloudProxy.ts)
        const foldedSrc = foldDevCloudSrc(src);
        const match = versions.find(v => v.uri &&
            (foldDevCloudSrc(v.uri.toString()) === foldedSrc || foldDevCloudSrc(v.uri.toString(true)) === foldedSrc));
        if (match) return match;
    }
    // The chrome extension inspects VANILLA three.js pages — no gltf-progressive
    // runtime, so a progressive variant would show its lowres base mesh forever.
    // Default to a non-progressive version there ("Optimized" beats "Original" by
    // size); an explicit user pick or a document-recorded src (above) always wins.
    let candidates = versions;
    if (BUILD_PLATFORM === "chrome-extension") {
        const nonProgressive = versions.filter(v => v.progressive !== true);
        if (nonProgressive.length > 0) candidates = nonProgressive;
    }
    // glTF preferred over other engine-loadable formats (fbx/obj versions are
    // listable now): smallest-by-size only decides WITHIN the preferred family —
    // an fbx smaller than every glb must not win the default
    const gltf = candidates.filter(v => !v.uri || isGltfFamilyUrl(v.uri.path) || (v.format ? GLTF_FAMILY_FORMATS.has(v.format) : false));
    if (gltf.length > 0) candidates = gltf;
    return [...candidates].sort((a, b) =>
        (a.totalSize ?? Number.MAX_VALUE) - (b.totalSize ?? Number.MAX_VALUE))[0];
}

export abstract class AbstractAssetsService extends Disposable implements IAssetsService {
    _serviceBrand: undefined;

    constructor(
        @IInstantiationService protected readonly instantiationService: IInstantiationService,
        @ILogService protected readonly logger: ILogService,
    ) {
        super();
    }

    protected readonly _onDidChange = this._register(new Emitter<void>());
    readonly onDidChange = this._onDidChange.event;

    notifyExternalChange(): void {
        this._onDidChange.fire();
    }

    abstract queryOptions(): Promise<AssetOptionsResult>;
    abstract list(options: AssetFetchOptions, token?: CancellationToken, onProgress?: (progress: AssetFetchProgress) => void): Promise<AssetFetchResult>;
    abstract loadAsset(id: string, token?: CancellationToken, provider?: string): Promise<FullAsset | null>;

    /** libraries that support importing override this (the project provider);
     *  remote catalogs are read-only */
    importAsset(_request: AssetImportRequest, _token?: CancellationToken): Promise<Asset | null> {
        return Promise.resolve(null);
    }

    /** see importAsset — read-only by default */
    deleteAsset(_asset: Asset, _token?: CancellationToken): Promise<boolean> {
        return Promise.resolve(false);
    }

    /**
     * Generic across providers: loads the asset, picks the version (by `src`, else the
     * smallest — the interaction handler's default), and returns its fetch base + a
     * fallback-wrapped url rewrite. The provider owns the actual rewrite (`version.resolveUrl`);
     * this only recovers + wraps it so both the drop/assign path and the document-reopen path
     * resolve resources through ONE method.
     */
    async resolveResources(ref: AssetResourceRef, token?: CancellationToken): Promise<AssetResourceResolution | null> {
        const full = await this.loadAsset(ref.id, token, ref.provider);
        if (!full || full.versions.length === 0) return null;
        const version = pickAssetVersion(full.versions, ref.src);
        if (!version?.uri) return null;
        const rewrite = version.resolveUrl;
        return {
            src: version.uri,
            resourcePath: version.resourcePath,
            resolveUrl: rewrite ? (url: string) => rewrite(url) || url : undefined,
        };
    }

    private readonly _interactionHandlers: IAssetInteractionHandler[] = [];

    registerInteractionHandler(handler: IAssetInteractionHandler): IDisposable {
        this._interactionHandlers.push(handler);
        return toDisposable(() => {
            const index = this._interactionHandlers.indexOf(handler);
            if (index >= 0) {
                this._interactionHandlers.splice(index, 1);
            }
        })
    }

    handleInteraction(interaction: AssetInteraction, token?: CancellationToken): Promise<boolean> | null {

        // Applying an asset (drop / click-to-apply / explicit targets) modifies
        // the scene — license-gated EVERYWHERE ("looking is free, working is
        // Pro"; browsing stays free).
        // Browsing and thumbnail previews never route through here.
        if (!this._guardApply(interaction)) {
            return Promise.resolve(false);
        }

        const promises = new Array<Promise<boolean>>();

        this.instantiationService.invokeFunction(accessor => {
            for (let i = this._interactionHandlers.length - 1; i >= 0; i--) {
                const handler = this._interactionHandlers[i];
                if (handler.canOpenAsset(accessor, interaction)) {
                    const promise = handler.openAsset(accessor, interaction, token);
                    if (promise) promises.push(promise);
                }
            }
        });


        if (promises.length <= 0) {
            this.logger.warn(`No handler found for asset interaction`, interaction);
            return null;
        }

        return Promise.allSettled(promises).then(results => {
            for (const result of results) {
                if (result.status === "fulfilled" && result.value) {
                    return true;
                }
            }
            return false;
        });
    }

    /** Set while an upsell dialog is pending so repeated attempts open it once. */
    private _upsellPending = false;

    /** Allow applying assets when the user holds an inspector entitlement
     *  (Needle Engine PRO or a Needle Inspector purchase); otherwise open the
     *  two-stage upsell (login → buy) once and veto. Applies EVERYWHERE
     *  (public sites included) — browsing stays free, changing a scene with
     *  assets is work. Services are resolved via the accessor (not
     *  ctor-injected) so provider subclasses keep their constructors;
     *  environments without licensing infra (tests) skip the gate. */
    private _guardApply(interaction: AssetInteraction): boolean {
        let allowed = true;
        this.instantiationService.invokeFunction(accessor => {
            const identity = accessor.getIfExists(IIdentitiyService);
            const license = accessor.getIfExists(ILicenseService);
            if (!identity || !license) return;
            if (license.isInspectorAccessAllowed) return;
            allowed = false;
            const asset = interaction.assets[0];
            const detail = `${interaction.type} ${asset ? `${asset.type}:${asset.provider ?? "?"}/${asset.id}` : "no-asset"}`;
            this.logger.info("AssetsService: apply gated", { detail, loggedIn: identity.isLoggedIn, licensed: license.isInspectorLicensed, accessReason: license.inspectorAccessReason });
            if (!this._upsellPending) {
                this._upsellPending = true;
                accessor.getIfExists(IAnalyticsService)?.logEvent("assets.apply.license_required", { detail, loggedIn: identity.isLoggedIn });
                if (CommandsRegistry.getCommand(OpenUpsellDialogCommandId)) {
                    const options: UpsellDialogOptions = {
                        source: "assets",
                        detail,
                        requiresLicense: true,
                        title: "Adding assets is a Pro feature",
                        subtitle: "Drag & drop materials and HDRIs straight into the running scene.",
                        message: "Applying assets requires Needle Inspector Pro (included with Needle Engine Pro). Sign in with a free account to continue.",
                        purchaseLabel: "Get Needle Inspector Pro",
                    };
                    accessor.get(ICommandService).executeCommand(OpenUpsellDialogCommandId, options);
                } else {
                    this.logger.warn("AssetsService: license required to apply assets (upsell command not registered).");
                }
                // Allow re-opening once the dialog settles.
                setTimeout(() => { this._upsellPending = false; }, 1500);
            }
        });
        return allowed;
    }
}
